<template>
  <div class="card-table">
    <b-table
      v-bind="$attrs"
      v-on="$listeners"
      :items="items"
      :fields="fields"
      :busy="busy"
      :primary-key="primaryKey"
      responsive
      borderless
      tbody-tr-class="card-table-row"
    >
      <template #table-busy>
        <slot name="table-busy">
          <div class="text-center py-3">{{ busyText }}</div>
        </slot>
      </template>

      <template #empty>
        <slot name="empty">
          <div class="text-center py-3">{{ emptyText }}</div>
        </slot>
      </template>

      <template
        v-for="slotName in forwardedSlotNames"
        v-slot:[slotName]="slotProps"
      >
        <slot :name="slotName" v-bind="slotProps" />
      </template>
    </b-table>
  </div>
</template>

<script>
/**
 * CardTable
 *
 * Wrapper genérico em torno do `<b-table>` do BootstrapVue que aplica o
 * visual padrão de "linhas em card" (bordas arredondadas, espaçamento
 * entre linhas, hover destacado). Não conhece nada sobre o domínio de
 * quem a usa — toda a customização é feita via `fields` + slots.
 *
 * Customização de conteúdo:
 * - `#cell(<key>)="{ item, ... }"`: conteúdo de uma célula específica,
 *   onde `<key>` é o `key` definido em `fields` (mesma API do `b-table`).
 *   Qualquer slot nomeado repassado ao componente é encaminhado
 *   automaticamente para o `b-table` interno, então basta declarar os
 *   slots que fizerem sentido para o seu `fields` — nenhum precisa ser
 *   registrado aqui.
 * - `#table-busy`: conteúdo customizado para o estado de carregamento
 *   (senão usa o texto de `busyText`).
 * - `#empty`: conteúdo customizado para quando `items` estiver vazio
 *   (senão usa o texto de `emptyText`).
 *
 * Qualquer outra prop/evento do `b-table` (ex.: `sort-by`, `filter`,
 * `@row-clicked`) pode ser passado diretamente no `<CardTable>` — são
 * repassados via `$attrs`/`$listeners` sem precisar ser declarados aqui.
 */
export default {
  name: "CardTable",

  inheritAttrs: false,

  props: {
    /** Lista de itens (linhas) a serem exibidos. */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Definição das colunas, mesmo formato do `fields` do `b-table`
     * (https://bootstrap-vue.org/docs/components/table#fields-column-definitions):
     * `{ key, label, thClass, tdClass, ... }`.
     */
    fields: {
      type: Array,
      required: true,
    },
    /** Exibe o estado de carregamento (slot `#table-busy`) enquanto `true`. */
    busy: {
      type: Boolean,
      default: false,
    },
    /** Campo único usado como chave de cada linha (`primary-key` do `b-table`). */
    primaryKey: {
      type: String,
      default: "id",
    },
    /** Texto padrão do estado de carregamento, usado se `#table-busy` não for passado. */
    busyText: {
      type: String,
      default: "Carregando",
    },
    /** Texto padrão para lista vazia, usado se `#empty` não for passado. */
    emptyText: {
      type: String,
      default: "Nenhum resultado encontrado",
    },
  },

  computed: {
    forwardedSlotNames() {
      return Object.keys(this.$scopedSlots).filter(
        (name) => !["table-busy", "empty"].includes(name)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.card-table {
  max-width: 1400px;
  margin: 0 auto;

  ::v-deep table {
    border-collapse: separate;
    border-spacing: 0 15px;
  }

  ::v-deep thead th {
    border: none;
    text-align: left;
    font-size: 16px;
    font-weight: 700;
    color: $grey1;
    padding: 0 16px 8px 16px;
    white-space: nowrap;
  }

  ::v-deep tbody tr.card-table-row td {
    background-color: $white;
    padding: 18px 16px;
    vertical-align: middle;
    font-size: 16px;
    border-top: 1px solid $grey5;
    border-bottom: 1px solid $grey5;

    &:first-child {
      border-left: 1px solid $grey5;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
      overflow: hidden;
    }
    &:last-child {
      border-right: 1px solid $grey5;
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
      overflow: hidden;
    }
  }

  ::v-deep tbody tr.card-table-row:hover td {
    border-color: $orange;
  }
}
</style>
